import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

// Providers stay eager – they are tiny and always needed
import { RingCentralDataProvider } from './context/RingCentralDataContext';
import { CompanyProvider } from './contexts/CompanyContext';

// ─── Layouts (lazy) ────────────────────────────────────────────────────────────
const ClinicWorkflowLayout     = React.lazy(() => import('./layouts/clinicflow/ClinicWorkflowLayout'));
const DocsLayout               = React.lazy(() => import('./layouts/docs/DocsLayout'));
const FirmDocsLayout           = React.lazy(() => import('./layouts/firm-docs/FirmDocsLayout'));
const MobileRouterComponent    = React.lazy(() => import('./MobileRouter'));

// ─── Clinic / Root pages (lazy) ────────────────────────────────────────────────
const FlowHomePage             = React.lazy(() => import('./layouts/clinicflow/FlowHomePage'));
const VoiceMailListPage        = React.lazy(() => import('./pages/VoiceMailListPage'));
const FaxListPage              = React.lazy(() => import('./pages/FaxListPage'));
const TagDashboardPage         = React.lazy(() => import('./components/TagDashboardPage'));
const ContextWorkWrapper       = React.lazy(() => import('./components/context/ContextWorkWrapper'));
const RichMessageViewerWrapper = React.lazy(() => import('./components/RichMessageViewerWrapper'));
const DynamicTriageWrapper     = React.lazy(() => import('./pages/DynamicTriageWrapper'));
const Schedule                 = React.lazy(() => import('./components/Schedule'));
const FileUploader             = React.lazy(() => import('./components/FileUploader'));
const PtDocsUploader           = React.lazy(() => import('./components/patient/PtDocsUploader'));
const DoctorHospitalRounds     = React.lazy(() => import('./components/claims/DoctorHospitalRounds'));
const ProfilePage              = React.lazy(() => import('./layouts/pages/ProfilePage'));
const RingCentralDashboardPage = React.lazy(() => import('./pages/ringcentral/RingCentralDashboardPage'));

// ─── Rounds (lazy) ─────────────────────────────────────────────────────────────
const DesktopRoundsEntryPage   = React.lazy(() => import('./pages/rounds/DesktopRoundsEntryPage'));

// ─── Claims / EMR (lazy) ───────────────────────────────────────────────────────
const ClaimsDashboardPage      = React.lazy(() => import('./pages/ClaimsDashboardPage'));
const ServiceDashboardPage     = React.lazy(() => import('./pages/ServiceDashboardPage'));
const UnifiedWorklistPage      = React.lazy(() => import('./pages/UnifiedWorklistPage'));
const CaseLogsPage             = React.lazy(() => import('./pages/CaseLogsPage'));
const Measure5Page             = React.lazy(() => import('./pages/Measure5Page'));
const MipsMeasuresDashboardPage= React.lazy(() => import('./pages/MipsMeasuresDashboardPage'));
const TempClaimManager         = React.lazy(() => import('./layouts/pages/TempClaimsManager'));
const TempClaimEditPage        = React.lazy(() => import('./components/claims/TempClaimEditPage'));
const TempClaimNewPage         = React.lazy(() => import('./components/claims/TempClaimNewPage'));
const ImportDoctorClaimsPage   = React.lazy(() => import('./components/claims/ImportDoctorClaimsPage'));
const PatientDemogParserPage   = React.lazy(() => import('./components/patient/PatientDemogParserPage'));
const ClaimListPage            = React.lazy(() => import('./components/emr/ClaimListPage'));
const ClaimDetailPage          = React.lazy(() => import('./components/emr/ClaimDetailPage'));
const PatientClaimsPage        = React.lazy(() => import('./components/emr/PatientClaimsPage'));
const PaymentsListPage         = React.lazy(() => import('./components/emr/PaymentsListPage'));
const DepositBatchesPage       = React.lazy(() => import('./components/deposit/DepositBatchesPage'));
const PaymentsDashboardPage    = React.lazy(() => import('./components/payments/PaymentsDashboardPage'));
const PaymentsReconciliationPage = React.lazy(() => import('./components/payments/PaymentsReconciliationPage'));
const HospitalTestSearchGrid   = React.lazy(() => import('./components/hosptest/HospitalTestSearchGrid'));
const RefundsPage              = React.lazy(() => import('./pages/RefundsPage'));
const InsurancePage            = React.lazy(() => import('./pages/InsurancePage'));

// ─── Patient (lazy) ────────────────────────────────────────────────────────────
const PtNewPatientEntry        = React.lazy(() => import('./components/patient/PtNewPatientEntry'));
const PtUpdatePatient          = React.lazy(() => import('./components/patient/PtUpdatePatient'));
const SelectPatientPage        = React.lazy(() => import('./components/patient/SelectPatientPage'));
const PtPatientDetails         = React.lazy(() => import('./components/patient/PtPatientDetails'));
const PtPatientList            = React.lazy(() => import('./components/patient/PtPatientList'));
const PatientDashboardPage     = React.lazy(() => import('./layouts/pages/PatientDashboardPage'));

// ─── ClickUp (lazy) ────────────────────────────────────────────────────────────
const ClickUpDashboardPage     = React.lazy(() => import('./components/clickup/ClickUpDashboardPage'));
const ClickUpMobileDashboard   = React.lazy(() => import('./components/clickup/ClickUpMobileDashboard'));
const ClickUpTimeLogHistoryPage= React.lazy(() => import('./components/clickup/ClickUpTimeLogHistoryPage'));

// ─── EMR settings / admin (lazy) ───────────────────────────────────────────────
const SettingsPage             = React.lazy(() => import('./pages/SettingsPage'));
const CodeListsPage            = React.lazy(() => import('./components/common/CodeListsPage'));
const PrivilegeManagerPage     = React.lazy(() => import('./components/common/PrivilegeManagerPage'));
const ContextEditorPage        = React.lazy(() => import('./pages/ContextEditorPage'));
const UserAuthGrantsPage       = React.lazy(() => import('./pages/UserAuthGrantsPage'));
const NavigationManagementPage = React.lazy(() => import('./pages/NavigationManagementPage'));
const ReferralUploadPage       = React.lazy(() => import('./pages/ReferralUploadPage'));
const JobRequestsDashboardPage = React.lazy(() => import('./pages/admin/JobRequestsDashboardPage'));
const ApiAuditLogsPage = React.lazy(() => import('./pages/admin/ApiAuditLogsPage'));
const GmailDashboardPage = React.lazy(() => import('./pages/admin/GmailDashboardPage'));
const InfraOpsDashboardPage = React.lazy(() => import('./pages/admin/InfraOpsDashboardPage'));
const InfraOpsRestorePage = React.lazy(() => import('./pages/admin/InfraOpsRestorePage'));
const EchoPacDashboardPage = React.lazy(() => import('./pages/admin/EchoPacDashboardPage'));
const EchoRepPage = React.lazy(() => import('./pages/admin/EchoRepPage'));
const EchoRepStudyPage = React.lazy(() => import('./pages/admin/EchoRepStudyPage'));
const EchoRepConfigPage = React.lazy(() => import('./pages/admin/EchoRepConfigPage'));
const DocuCheckPage = React.lazy(() => import('./pages/admin/DocuCheckPage'));
const DocuCheckFaxStepperPage = React.lazy(() => import('./pages/admin/DocuCheckFaxStepperPage'));
const AccessCatalogPage        = React.lazy(() => import('./pages/AccessCatalogPage'));

// ─── AGI (lazy) ────────────────────────────────────────────────────────────────
const AgiDashboardPage                       = React.lazy(() => import('./agi/pages/AgiDashboardPage'));
const AgiRunsPage                            = React.lazy(() => import('./agi/pages/AgiRunsPage'));
const AgiLlmCallsPage                        = React.lazy(() => import('./agi/pages/AgiLlmCallsPage'));
const ApexAgentChatPage                      = React.lazy(() => import('./agi/pages/ApexAgentChatPage'));
const AgiReviewQueuePage                     = React.lazy(() => import('./agi/pages/AgiReviewQueuePage'));
const AgiContentObjectsPage                  = React.lazy(() => import('./agi/pages/AgiContentObjectsPage'));
const AgiTriageReviewPage                    = React.lazy(() => import('./agi/pages/AgiTriageReviewPage'));
const AgiTemplatesPage                       = React.lazy(() => import('./agi/pages/AgiTemplatesPage'));
const AgiUsagePage                           = React.lazy(() => import('./agi/pages/AgiUsagePage'));
const AgiOrchestrationPage                   = React.lazy(() => import('./agi/pages/AgiOrchestrationPage'));
const AgiCapabilitiesPage                    = React.lazy(() => import('./agi/pages/AgiCapabilitiesPage'));
const AgiClickUpTaskAssistantPage            = React.lazy(() => import('./agi/pages/AgiClickUpTaskAssistantPage'));
const AgiEncounterAudioReviewPage            = React.lazy(() => import('./agi/pages/AgiEncounterAudioReviewPage'));
const AgiVisitFlowPage                       = React.lazy(() => import('./agi/pages/AgiVisitFlowPage'));
const AgiVisitFlowEncounterQuickReviewPage   = React.lazy(() => import('./agi/pages/AgiVisitFlowEncounterQuickReviewPage'));
const AgiVisitFlowNoteRulesPage              = React.lazy(() => import('./agi/pages/AgiVisitFlowNoteRulesPage'));
const AgiNotesWorkPage                       = React.lazy(() => import('./agi/pages/AgiNotesWorkPage'));
const AgiVisitTasksPage                      = React.lazy(() => import('./agi/pages/AgiVisitTasksPage'));
const AgiPendingNotesDashboardPage           = React.lazy(() => import('./agi/pages/AgiPendingNotesDashboardPage'));
const AgiPatientTestsDashboardPage           = React.lazy(() => import('./agi/pages/AgiPatientTestsDashboardPage'));
const AgiVisitReasonTestMappingsPage         = React.lazy(() => import('./agi/pages/AgiVisitReasonTestMappingsPage'));
const AgiCareGapsDashboardPage               = React.lazy(() => import('./agi/pages/AgiCareGapsDashboardPage'));
const AgiCareGapProtocolsPage                = React.lazy(() => import('./agi/pages/AgiCareGapProtocolsPage'));
const AgiReadyForLockWorklistPage            = React.lazy(() => import('./agi/pages/AgiReadyForLockWorklistPage'));
const AgiAllVisitsReviewPage                 = React.lazy(() => import('./agi/pages/AgiAllVisitsReviewPage'));

// ─── Books (lazy) ──────────────────────────────────────────────────────────────
const AccountsPage             = React.lazy(() => import('./pages/books/AccountsPage'));
const TransactionsPage         = React.lazy(() => import('./pages/books/TransactionsPageNew'));
const CompanyTransactionsPage  = React.lazy(() => import('./pages/books/CompanyTransactionsPage'));
const CompaniesPage            = React.lazy(() => import('./pages/books/CompaniesPage'));
const CompanyAccessPage        = React.lazy(() => import('./pages/books/CompanyAccessPage'));
const AccountAccessPage        = React.lazy(() => import('./pages/books/AccountAccessPage'));
const ChartOfAccountsPage      = React.lazy(() => import('./pages/books/ChartOfAccountsPage'));
const JournalEntriesPage       = React.lazy(() => import('./pages/books/JournalEntriesPage'));
const ReconciliationPage       = React.lazy(() => import('./pages/books/ReconciliationPage'));
const PlaidPage                = React.lazy(() => import('./pages/books/PlaidPage'));
const QuarantinePage           = React.lazy(() => import('./pages/books/QuarantinePage'));
const UsersPage                = React.lazy(() => import('./pages/books/UsersPage'));
const ReportsPage              = React.lazy(() => import('./pages/books/ReportsPage'));

// ─── Invest Control Plane (lazy) ────────────────────────────────────────────────
const InvestDecisionJournalPage  = React.lazy(() => import('./pages/books/InvestDecisionJournalPage'));
const InvestPaperTradesPage      = React.lazy(() => import('./pages/books/InvestPaperTradesPage'));
const InvestPortfolioSnapshotsPage = React.lazy(() => import('./pages/books/InvestPortfolioSnapshotsPage'));
const InvestRiskPolicyPage       = React.lazy(() => import('./pages/books/InvestRiskPolicyPage'));

// ─── Docs (lazy) ───────────────────────────────────────────────────────────────
const DocsViewer               = React.lazy(() => import('./components/docs/DocsViewer'));
const DocsEditor               = React.lazy(() => import('./components/docs/DocsEditor'));
const ManageDocumentRoots      = React.lazy(() => import('./components/docs/ManageDocumentRoots'));

// ─── Firm Docs (lazy) ──────────────────────────────────────────────────────────
const ApexHeartCareDocsPage    = React.lazy(() => import('./pages/firm-docs/ApexHeartCareDocsPage'));
const MBInvestDocsPage         = React.lazy(() => import('./pages/firm-docs/MBInvestDocsPage'));
const Heart360LifeDocsPage     = React.lazy(() => import('./pages/firm-docs/Heart360LifeDocsPage'));
const SnehaRealtyDocsPage      = React.lazy(() => import('./pages/firm-docs/SnehaRealtyDocsPage'));
const FirmDocsAdminPage        = React.lazy(() => import('./components/firm-docs/FirmDocsAdminPage'));

// ─── RPM / Heart360 (lazy) ─────────────────────────────────────────────────────
const RpmDashboardPage         = React.lazy(() => import('./pages/rpm/RpmDashboardPage'));
const RpmManagerPage           = React.lazy(() => import('./pages/rpm/RpmManagerPage'));
const RpmEnrollmentsPage       = React.lazy(() => import('./pages/rpm/RpmEnrollmentsPage'));
const RpmEnrollmentNewPage     = React.lazy(() => import('./pages/rpm/RpmEnrollmentNewPage'));
const RpmTimeLogsPage          = React.lazy(() => import('./pages/rpm/RpmTimeLogsPage'));
const RpmReportsPage           = React.lazy(() => import('./pages/rpm/RpmReportsPage'));
const RpmCalendarPage          = React.lazy(() => import('./pages/rpm/RpmCalendarPage'));
const CcmManagerPage           = React.lazy(() => import('./pages/rpm/CcmManagerPage'));
const RehabPage                = React.lazy(() => import('./pages/rpm/RehabPage'));
const VcrPage                  = React.lazy(() => import('./pages/rpm/VcrPage'));
const PatientSummaryPage       = React.lazy(() => import('./pages/rpm/PatientSummaryPage'));
const RpmDevicesPage           = React.lazy(() => import('./pages/rpm/RpmDevicesPage'));
const RpmBillingPage           = React.lazy(() => import('./pages/rpm/RpmBillingPage'));
const PracticeDashboardPage    = React.lazy(() => import('./pages/rpm/PracticeDashboardPage'));
const LifestyleDashboardPage   = React.lazy(() => import('./pages/rpm/LifestyleDashboardPage'));
const CcmDashboardPage         = React.lazy(() => import('./pages/rpm/CcmDashboardPage'));
const IcrDashboardPage         = React.lazy(() => import('./pages/rpm/IcrDashboardPage'));
const PatientPortalPage        = React.lazy(() => import('./pages/rpm/PatientPortalPage'));
const Heart360SettingsPage     = React.lazy(() => import('./pages/rpm/Heart360SettingsPage'));

// ─── Mobile (lazy) ─────────────────────────────────────────────────────────────
const MobileClinicPage         = React.lazy(() => import('./pages/mobile/MobileClinicPage'));
const MobileHospVisitPage      = React.lazy(() => import('./pages/mobile/MobileHospVisitPage'));
const MobileEmrPage            = React.lazy(() => import('./pages/mobile/MobileEmrPage'));
const MobileLandingPage        = React.lazy(() => import('./pages/mobile/MobileLandingPage'));
const MobileSchedulePage       = React.lazy(() => import('./pages/schedule/MobileSchedulePage'));
const MobilePatientDocsPage    = React.lazy(() => import('./pages/schedule/MobilePatientDocsPage'));
const MobileDocumentViewerPage = React.lazy(() => import('./pages/schedule/MobileDocumentViewerPage'));
const MobilePatientClaimPage   = React.lazy(() => import('./pages/rounds/MobilePatientClaimPage'));
const MobileProcedureEditPage  = React.lazy(() => import('./pages/rounds/MobileProcedureEditPage'));
const MobileClaimStatusPage    = React.lazy(() => import('./pages/rounds/MobileClaimStatusPage'));
const MobileRoundsEntryPage    = React.lazy(() => import('./pages/rounds/MobileRoundsEntryPage'));

// ─── Loading fallback ──────────────────────────────────────────────────────────
const PageLoader: React.FC = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={40} />
    </Box>
);

const AppRoutes: React.FC = () => {
    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                {/* Root landing */}
                <Route
                    path="/"
                    element={
                        <RingCentralDataProvider>
                            <ClinicWorkflowLayout />
                        </RingCentralDataProvider>
                    }
                >
                    <Route index element={<FlowHomePage />} />
                    <Route path="rcdash" element={<RingCentralDashboardPage />} />
                    <Route path="rcvm" element={<DynamicTriageWrapper />} />
                    <Route path="rcfax" element={<DynamicTriageWrapper />} />
                    <Route path="vmandfax" element={<DynamicTriageWrapper />} />

                    <Route path="sms" element={<DynamicTriageWrapper />} />
                    <Route path="calllog" element={<DynamicTriageWrapper />} />
                    <Route path="tasks" element={<DynamicTriageWrapper />} />
                    <Route path="referral" element={<DynamicTriageWrapper />} />
                    <Route path="fax" element={<FaxListPage />} />
                    <Route path="dashboard" element={<TagDashboardPage />} />
                    <Route path="schedule" element={<Schedule />} />
                    <Route path="context/:contextType/:contextId" element={<ContextWorkWrapper />} />
                    <Route path="message/:id" element={<RichMessageViewerWrapper />} />

                    <Route path="scandoc" element={<DynamicTriageWrapper />} />
                    <Route path="sdocupload" element={<FileUploader />} />
                    <Route path="ptdocupload" element={<PtDocsUploader />} />

                    <Route path="voicemail" element={<VoiceMailListPage />} />
                    <Route path="dynamictriage" element={<DynamicTriageWrapper />} />

                    {/* ClickUp Dashboard */}
                    <Route path="clickup" element={<ClickUpDashboardPage />} />
                    <Route path="clickup/time-log-history" element={<ClickUpTimeLogHistoryPage />} />

                    {/* Patient Management */}
                    <Route path="patient/new" element={<PtNewPatientEntry />} />
                    <Route path="patient/update" element={<PtUpdatePatient />} />
                    <Route path="patient/documents" element={<PtDocsUploader />} />
                    <Route path="patient/select" element={<SelectPatientPage />} />
                    <Route path="patient/details/:patientId" element={<PtPatientDetails />} />
                    <Route path="patient/list" element={<PtPatientList />} />
                    <Route path="patient/dashboard" element={<PatientDashboardPage />} />
                    <Route path="patients" element={<PatientDashboardPage />} />

                    <Route path="hospvisit/ptdocupload" element={<PtDocsUploader />} />
                    <Route path="hospvisit/superbill" element={<DoctorHospitalRounds />} />
                    <Route path="hospvisit/profile" element={<ProfilePage />} />
                    <Route path="hospvisit/roundsentry" element={<DesktopRoundsEntryPage />} />
                    <Route path="hospvisit/rounds/edit/:patientId?" element={<DesktopRoundsEntryPage />} />

                    {/* Temp Claims */}
                    <Route path="hospvisit/tempclaims" element={<TempClaimManager />} />
                    <Route path="hospvisit/tempclaims/new" element={<TempClaimNewPage />} />
                    <Route path="hospvisit/tempclaims/edit/:tclaimId" element={<TempClaimEditPage />} />
                    <Route path="hospvisit/tempclaims/import-doctor-claims" element={<ImportDoctorClaimsPage />} />
                    <Route path="hospvisit/demogparse" element={<PatientDemogParserPage />} />

                    <Route path="emr/claims" element={<ClaimListPage />} />
                    <Route path="claims-dashboard" element={<ClaimsDashboardPage />} />
                    <Route path="service-dashboard" element={<ServiceDashboardPage />} />
                    <Route path="unified-worklist" element={<UnifiedWorklistPage />} />
                    <Route path="apex-reports/case-logs" element={<CaseLogsPage />} />
                    <Route path="mips/measure5" element={<Measure5Page />} />
                    <Route path="mips/measures" element={<MipsMeasuresDashboardPage />} />

                    {/* Referral workflows */}
                    <Route path="referrals/upload" element={<ReferralUploadPage />} />

                    {/* AGI (Agentic) */}
                    <Route path="agi/dashboard" element={<AgiDashboardPage />} />
                    <Route path="agi/runs" element={<AgiRunsPage />} />
                    <Route path="agi/llm-calls" element={<AgiLlmCallsPage />} />
                    <Route path="agi/chat" element={<ApexAgentChatPage />} />
                    <Route path="agi/reviews" element={<AgiReviewQueuePage />} />
                    <Route path="agi/content-objects" element={<AgiContentObjectsPage />} />
                    <Route path="agi/triage-review" element={<AgiTriageReviewPage />} />
                    <Route path="agi/templates" element={<AgiTemplatesPage />} />
                    <Route path="agi/usage" element={<AgiUsagePage />} />
                    <Route path="agi/orchestration" element={<AgiOrchestrationPage />} />
                    <Route path="agi/capabilities" element={<AgiCapabilitiesPage />} />
                    <Route path="agi/task-assistant" element={<AgiClickUpTaskAssistantPage />} />
                    <Route path="agi/encounter-audio/review/:encounterId" element={<AgiEncounterAudioReviewPage />} />
                    <Route path="visit-flow" element={<AgiVisitFlowPage />} />
                    <Route path="visit-flow/:encounterId" element={<AgiVisitFlowPage />} />
                    <Route path="visit-flow/:encounterId/quick-review" element={<AgiVisitFlowEncounterQuickReviewPage />} />
                    <Route path="agi/visit-flow/note-rules" element={<AgiVisitFlowNoteRulesPage />} />
                    <Route path="agi/notes-work" element={<AgiNotesWorkPage />} />
                    <Route path="agi/visit-tasks" element={<AgiVisitTasksPage />} />
                    <Route path="agi/pending-notes" element={<AgiPendingNotesDashboardPage />} />
                    <Route path="agi/patient-tests" element={<AgiPatientTestsDashboardPage />} />
                    <Route path="agi/patient-tests/reason-mappings" element={<AgiVisitReasonTestMappingsPage />} />
                    <Route path="agi/care-gaps" element={<AgiCareGapsDashboardPage />} />
                    <Route path="agi/care-gaps/protocols" element={<AgiCareGapProtocolsPage />} />
                    <Route path="agi/ready-for-lock" element={<AgiReadyForLockWorklistPage />} />
                    <Route path="agi/all-visits" element={<AgiAllVisitsReviewPage />} />
                    <Route path="admin/jobs" element={<JobRequestsDashboardPage />} />
                    <Route path="admin/api-logs" element={<ApiAuditLogsPage />} />
                    <Route path="admin/gmail" element={<GmailDashboardPage />} />
                    <Route path="admin/infra-ops" element={<InfraOpsDashboardPage />} />
                    <Route path="admin/infra-ops/restore" element={<InfraOpsRestorePage />} />
                    <Route path="admin/echopac" element={<EchoPacDashboardPage />} />
                    <Route path="admin/echo-rep" element={<EchoRepPage />} />
                    <Route path="admin/echo-rep/study" element={<EchoRepStudyPage />} />
                    <Route path="admin/echo-rep/config" element={<EchoRepConfigPage />} />
                    <Route path="admin/docu-check" element={<DocuCheckPage />} />
                    <Route path="admin/docu-check-fax-stepper" element={<DocuCheckFaxStepperPage />} />

                    {/* Other routes */}
                    <Route path="emr/usergrants" element={<UserAuthGrantsPage />} />
                    <Route path="emr/configeditor" element={<ContextEditorPage />} />
                    <Route path="emr/settings" element={<SettingsPage />} />
                    <Route path="emr/deposits" element={<DepositBatchesPage />} />
                    <Route path="emr/privileges" element={<PrivilegeManagerPage />} />
                    <Route path="emr/navigation" element={<NavigationManagementPage />} />
                    <Route path="emr/codelists" element={<CodeListsPage />} />
                    <Route path="emr/payments/:paymentid" element={<PaymentsListPage />} />
                    <Route path="emr/claim/:claimId" element={<ClaimDetailPage />} />
                    <Route path="emr/claim/encounter/:encounterId" element={<ClaimDetailPage />} />
                    <Route path="emr/patient/:patientId/claims" element={<PatientClaimsPage />} />
                    <Route path="emr/payments" element={<PaymentsListPage />} />
                    <Route path="emr/pmt/dashboard" element={<PaymentsDashboardPage />} />
                    <Route path="emr/pmt/reconcile" element={<PaymentsReconciliationPage />} />
                    <Route path="/emr/hospital-tests" element={<HospitalTestSearchGrid />} />
                    <Route path="emr/refunds" element={<RefundsPage />} />
                    <Route path="emr/insurance" element={<InsurancePage />} />

                    <Route path="emr/progress-notes" element={<Navigate to="/agi/notes-work" replace />} />

                    {/* Books (Apex Books) Routes */}
                    <Route path="books/*" element={
                        <CompanyProvider>
                            <Routes>
                                <Route path="users" element={<UsersPage />} />
                                <Route path="accounts" element={<AccountsPage />} />
                                <Route path="transactions" element={<TransactionsPage />} />
                                <Route path="company-transactions" element={<CompanyTransactionsPage />} />
                                <Route path="companies" element={<CompaniesPage />} />
                                <Route path="company-access" element={<CompanyAccessPage />} />
                                <Route path="account-access" element={<AccountAccessPage />} />
                                <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
                                <Route path="journal-entries" element={<JournalEntriesPage />} />
                                <Route path="reconciliation" element={<ReconciliationPage />} />
                                <Route path="plaid" element={<PlaidPage />} />
                                <Route path="quarantine" element={<QuarantinePage />} />
                                <Route path="reports" element={<ReportsPage />} />
                                {/* Invest Control Plane */}
                                <Route path="invest/decision-journal" element={<InvestDecisionJournalPage />} />
                                <Route path="invest/paper-trades" element={<InvestPaperTradesPage />} />
                                <Route path="invest/portfolio-snapshots" element={<InvestPortfolioSnapshotsPage />} />
                                <Route path="invest/risk-policy" element={<InvestRiskPolicyPage />} />
                            </Routes>
                        </CompanyProvider>
                    } />

                    {/* Firm Docs admin route moved to minimal layout below */}

                </Route>

                {/* Docs Routes */}
                <Route path="/docs" element={<DocsLayout />}>
                    <Route path="manage-roots" element={<ManageDocumentRoots />} />
                    <Route path=":siteSlug/edit/*" element={<DocsEditor />} />
                    <Route path=":siteSlug/*" element={<DocsViewer />} />
                    <Route index element={<Navigate to="/" replace />} />
                </Route>

                {/* ── Firm Docs — minimal layout (doc-viewer nav only, no workflow chrome) ── */}
                <Route path="/firm-docs" element={<FirmDocsLayout />}>
                    <Route path="apexheartcare" element={<ApexHeartCareDocsPage />} />
                    <Route path="mbinvest"      element={<MBInvestDocsPage />} />
                    <Route path="heart360life"  element={<Heart360LifeDocsPage />} />
                    <Route path="sneharealty"   element={<SnehaRealtyDocsPage />} />
                    {/* Admin Review now uses minimal layout */}
                    <Route path="admin" element={<FirmDocsAdminPage />} />
                </Route>

                {/* heart360 / Lifestyle Routes */}
                <Route path="/heart360/lifestyle" element={<LifestyleDashboardPage />} />
                <Route path="/heart360/lifestyle/manager" element={<div>Lifestyle Manager (TODO)</div>} />
                <Route path="/heart360/lifestyle/patients" element={<div>Lifestyle Patients (TODO)</div>} />
                <Route path="/heart360/lifestyle/preventive" element={<div>Preventive Care (TODO)</div>} />
                <Route path="/heart360/lifestyle/monitoring" element={<div>Monitoring (TODO)</div>} />
                <Route path="/heart360/lifestyle/wise-choices" element={<div>Wise Choices (TODO)</div>} />
                <Route path="/heart360/lifestyle/nutrition" element={<div>Nutrition (TODO)</div>} />
                <Route path="/heart360/lifestyle/fitness" element={<div>Fitness (TODO)</div>} />
                <Route path="/heart360/lifestyle/reports" element={<RpmReportsPage />} />
                <Route path="/heart360/lifestyle/calendar" element={<RpmCalendarPage />} />
                <Route path="/heart360/lifestyle/billing" element={<RpmBillingPage />} />

                <Route path="/heart360" element={<PracticeDashboardPage />} />
                <Route path="/heart360/" element={<PracticeDashboardPage />} />
                <Route path="/heart360/practice" element={<PracticeDashboardPage />} />

                {/* RPM Routes */}
                <Route path="/heart360/rpm" element={<RpmDashboardPage />} />
                <Route path="/heart360/rpm/manager" element={<RpmManagerPage />} />
                <Route path="/heart360/rpm/dashboard" element={<RpmDashboardPage />} />
                <Route path="/heart360/rpm/enrollments" element={<RpmEnrollmentsPage />} />
                <Route path="/heart360/rpm/enrollments/new" element={<RpmEnrollmentNewPage />} />
                <Route path="/heart360/rpm/enrollments/:enrollmentId" element={<div>Enrollment Detail Page (TODO)</div>} />
                <Route path="/heart360/rpm/billing" element={<RpmBillingPage />} />
                <Route path="/heart360/rpm/billing/:enrollmentId" element={<RpmBillingPage />} />
                <Route path="/heart360/rpm/devices" element={<RpmDevicesPage />} />
                <Route path="/heart360/rpm/time-logs" element={<RpmTimeLogsPage />} />
                <Route path="/heart360/rpm/time-logs/:patientId" element={<RpmTimeLogsPage />} />
                <Route path="/heart360/rpm/reports" element={<RpmReportsPage />} />
                <Route path="/heart360/rpm/calendar" element={<RpmCalendarPage />} />
                <Route path="/heart360/rpm/patients/:enrollmentId/summary" element={<PatientSummaryPage />} />

                {/* CCM Routes */}
                <Route path="/heart360/ccm" element={<CcmDashboardPage />} />
                <Route path="/heart360/ccm/manager" element={<CcmManagerPage />} />
                <Route path="/heart360/ccm/patients" element={<CcmManagerPage />} />
                <Route path="/heart360/ccm/reports" element={<RpmReportsPage />} />
                <Route path="/heart360/ccm/calendar" element={<RpmCalendarPage />} />
                <Route path="/heart360/ccm/billing" element={<RpmBillingPage />} />

                {/* ICR Routes */}
                <Route path="/heart360/icr" element={<IcrDashboardPage />} />
                <Route path="/heart360/icr/rehab" element={<RehabPage />} />
                <Route path="/heart360/icr/rehab/:enrollmentId" element={<RehabPage />} />
                <Route path="/heart360/icr/vcr" element={<VcrPage />} />
                <Route path="/heart360/icr/patients" element={<RehabPage />} />
                <Route path="/heart360/icr/patients/all" element={<RehabPage />} />
                <Route path="/heart360/icr/reports" element={<RpmReportsPage />} />
                <Route path="/heart360/icr/calendar" element={<RpmCalendarPage />} />
                <Route path="/heart360/icr/billing" element={<RpmBillingPage />} />

                {/* Shared Routes */}
                <Route path="/heart360/enrollments" element={<RpmEnrollmentsPage />} />
                <Route path="/heart360/manager" element={<RpmManagerPage />} />
                <Route path="/heart360/calendar" element={<RpmCalendarPage />} />
                <Route path="/heart360/reports" element={<RpmReportsPage />} />
                <Route path="/heart360/billing" element={<RpmBillingPage />} />
                <Route path="/heart360/patients/:enrollmentId/summary" element={<PatientSummaryPage />} />

                {/* Patient Portal */}
                <Route path="/heart360/patient" element={<PatientPortalPage />} />
                <Route path="/heart360/patient/:patientId" element={<PatientPortalPage />} />

                {/* Heart360 Settings */}
                <Route path="/heart360/settings" element={<Heart360SettingsPage />} />

                {/* Legacy RPM Routes */}
                <Route path="/rpm" element={<RpmManagerPage />} />
                <Route path="/rpm/manager" element={<RpmManagerPage />} />
                <Route path="/rpm/dashboard" element={<RpmDashboardPage />} />
                <Route path="/rpm/enrollments" element={<RpmEnrollmentsPage />} />
                <Route path="/rpm/enrollments/new" element={<RpmEnrollmentNewPage />} />
                <Route path="/rpm/enrollments/:enrollmentId" element={<div>Enrollment Detail Page (TODO)</div>} />
                <Route path="/rpm/billing" element={<RpmBillingPage />} />
                <Route path="/rpm/billing/:enrollmentId" element={<RpmBillingPage />} />
                <Route path="/rpm/devices" element={<RpmDevicesPage />} />
                <Route path="/rpm/time-logs" element={<RpmTimeLogsPage />} />
                <Route path="/rpm/time-logs/:patientId" element={<RpmTimeLogsPage />} />
                <Route path="/rpm/reports" element={<RpmReportsPage />} />
                <Route path="/rpm/calendar" element={<RpmCalendarPage />} />
                <Route path="/rpm/ccm" element={<CcmManagerPage />} />
                <Route path="/rpm/rehab" element={<RehabPage />} />
                <Route path="/rpm/rehab/:enrollmentId" element={<RehabPage />} />
                <Route path="/rpm/vcr" element={<VcrPage />} />
                <Route path="/rpm/patients/:enrollmentId/summary" element={<PatientSummaryPage />} />

                {/* Access Catalog */}
                <Route path="/access-catalog" element={<AccessCatalogPage />} />


                {/* Mobile routes */}
                <Route path="/mobile" element={<MobileRouterComponent />}>
                    <Route index element={<MobileLandingPage />} />
                    <Route path="clinic" element={<MobileClinicPage />} />
                    <Route path="hospvisit" element={<MobileHospVisitPage />} />
                    <Route path="emr" element={<MobileEmrPage />} />
                    <Route path="rounds" element={<MobileRoundsEntryPage />} />
                    <Route path="roundsentry" element={<MobileRoundsEntryPage />} />
                    <Route path="schedule" element={<MobileSchedulePage />} />
                    <Route path="clickup" element={<ClickUpMobileDashboard />} />

                    <Route path="emr/schedule" element={<MobileSchedulePage />} />
                    <Route path="emr/docs/:patientID" element={<MobilePatientDocsPage />} />
                    <Route path="emr/docviewer/:patientID/:filepath" element={<MobileDocumentViewerPage />} />


                    <Route path="rounds/claim/:patientClaimId" element={<MobilePatientClaimPage />} />
                    <Route path="rounds/claim/:patientClaimId/add" element={<MobileProcedureEditPage />} />
                    <Route path="rounds/claim/:patientClaimId/edit/:claimDetailId" element={<MobileProcedureEditPage />} />
                    <Route path="rounds/claim/:patientClaimId/status" element={<MobileClaimStatusPage />} />

                    <Route path="rounds/desktop" element={<DesktopRoundsEntryPage />} />
                    <Route path="rounds/desktop/edit/:patientId?" element={<DesktopRoundsEntryPage />} />

                    <Route path="rounds/mobile" element={<MobileRoundsEntryPage />} />
                    <Route path="rounds/mobile/claim/:patientClaimId" element={<MobilePatientClaimPage />} />
                    <Route path="rounds/mobile/claim/:patientClaimId/add" element={<MobileProcedureEditPage />} />
                    <Route path="rounds/mobile/claim/:patientClaimId/edit/:claimDetailId" element={<MobileProcedureEditPage />} />
                    <Route path="rounds/mobile/claim/:patientClaimId/status" element={<MobileClaimStatusPage />} />
                </Route>

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
};

export default AppRoutes;
